import { motion } from "framer-motion";
import { Check, Circle, Lock, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface PipelineStageProps {
  stage: number;
  title: string;
  description: string;
  isCompleted: boolean;
  isActive: boolean;
  isLocked: boolean;
  isPartial?: boolean;
  partialText?: string;
  onClick?: () => void;
}

export function PipelineStage({
  stage,
  title,
  description,
  isCompleted,
  isActive,
  isLocked,
  isPartial,
  partialText,
  onClick,
}: PipelineStageProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: stage * 0.1 }}
      className={cn(
        "relative p-4 rounded-xl border-2 transition-all duration-300",
        isCompleted && "bg-status-completed/10 border-status-completed cursor-pointer hover:bg-status-completed/20",
        isPartial && !isCompleted && "bg-status-partial/10 border-status-partial cursor-pointer",
        isActive && !isCompleted && !isPartial && "bg-primary/5 border-primary animate-pulse-glow cursor-pointer",
        isLocked && "bg-muted/50 border-border opacity-60 cursor-not-allowed",
        !isCompleted && !isActive && !isLocked && !isPartial && "bg-card border-border hover:border-primary/50 cursor-pointer"
      )}
      onClick={isLocked ? undefined : onClick}
    >
      {/* Connector Line */}
      {stage < 6 && (
        <div
          className={cn(
            "absolute left-8 top-full w-0.5 h-4 -translate-x-1/2",
            isCompleted ? "bg-status-completed" : isPartial ? "bg-status-partial" : "bg-border"
          )}
        />
      )}

      <div className="flex items-start gap-4">
        {/* Stage Indicator */}
        <div
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all",
            isCompleted && "bg-status-completed text-white",
            isPartial && !isCompleted && "bg-status-partial text-white",
            isActive && !isCompleted && !isPartial && "gradient-accent text-white",
            isLocked && "bg-muted text-muted-foreground",
            !isCompleted && !isActive && !isLocked && !isPartial && "bg-secondary text-secondary-foreground"
          )}
        >
          {isCompleted ? (
            <Check className="w-5 h-5" />
          ) : isLocked ? (
            <Lock className="w-4 h-4" />
          ) : (
            <span className="font-display text-lg">{stage}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "font-display text-lg tracking-wide mb-1",
              isCompleted && "text-status-completed",
              isPartial && !isCompleted && "text-status-partial",
              isActive && !isCompleted && !isPartial && "text-primary",
              isLocked && "text-muted-foreground"
            )}
          >
            {title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
          {/* Click to view hint for completed stages */}
          {isCompleted && (
            <p className="text-xs text-status-completed mt-1 flex items-center gap-1">
              <span>Clique para ver detalhes</span>
              <ChevronRight className="w-3 h-3" />
            </p>
          )}
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0">
          {isCompleted && (
            <span className="text-xs font-medium text-status-completed bg-status-completed/10 px-2 py-1 rounded-full">
              Concluída
            </span>
          )}
          {isPartial && !isCompleted && (
            <span className="text-xs font-medium text-status-partial bg-status-partial/10 px-2 py-1 rounded-full">
              {partialText || "Parcial"}
            </span>
          )}
          {isActive && !isCompleted && !isPartial && (
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
              Em andamento
            </span>
          )}
          {isLocked && (
            <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
              Bloqueada
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
